import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  withStyles,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import { Button, Chip } from '@orijinworks/frontend-commons';
import { CANVAS_ACCESS_ROW_TOOLTIP } from '../../constants/canvas-access.constants';
import { buildStaffCanvasAccessRowViewModel } from '../utils/canvas-access.utils';
import styles from './StaffCanvasAccessRow.style';

/**
 * Display-only row for a staff member's Canvas Access at one location.
 */
const StaffCanvasAccessRow = ({
  classes,
  row,
  editingLocationKey = null,
  roleOptionsByLocation,
  rowActionsDisabled,
  draftRoleId = null,
  isSelected,
  isLoadingRoles = false,
  submitting,
  onToggleSelected,
  onLoadRoles,
  onDraftRoleChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onRemoveAccess = null,
}) => {
  const viewModel = useMemo(
    () => ({
      ...buildStaffCanvasAccessRowViewModel(row, {
        editingLocationKey,
        roleOptionsByLocation,
        rowActionsDisabled,
      }),
      draftRoleId,
      isSelected,
      submitting,
    }),
    [row, editingLocationKey, roleOptionsByLocation, rowActionsDisabled, draftRoleId, isSelected, submitting],
  );

  let actionIcon;
  if (isLoadingRoles) {
    actionIcon = <CircularProgress size={20} />;
  } else if (row.provisioned) {
    actionIcon = <EditIcon color="primary" />;
  } else {
    actionIcon = <CloudUploadIcon color="primary" />;
  }

  const showRemove = Boolean(row.provisioned && onRemoveAccess && !viewModel.isEditing);

  return (
    <TableRow
      selected={viewModel.isSelected}
      className={viewModel.isCanvasReady ? undefined : classes.notReadyRow}
    >
      <TableCell padding="checkbox">
        {viewModel.isCanvasReady && (
          <Checkbox
            checked={viewModel.isSelected}
            disabled={viewModel.disabled || viewModel.isEditing}
            onChange={onToggleSelected}
          />
        )}
      </TableCell>
      <TableCell className={viewModel.isCanvasReady ? undefined : classes.mutedText}>
        {row.locationName || row.locationKey}
      </TableCell>
      <TableCell>
        <Box display="inline-flex">
          <Chip
            size="small"
            variant="outlined"
            color={viewModel.statusTier}
            label={viewModel.statusLabel}
          />
        </Box>
      </TableCell>
      <TableCell className={viewModel.isCanvasReady ? undefined : classes.mutedText}>
        {viewModel.isEditing ? (
          <Select
            fullWidth
            displayEmpty
            value={viewModel.draftRoleId ?? ''}
            onChange={onDraftRoleChange}
            onOpen={onLoadRoles}
          >
            <MenuItem value="" disabled>
              Select role
            </MenuItem>
            {viewModel.roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          viewModel.roleLabel
        )}
      </TableCell>
      <TableCell align="right">
        {viewModel.isEditing ? (
          <Box display="flex" justifyContent="flex-end" gridGap={8}>
            <Button size="small" variant="outlined" onClick={onCancelEdit} disabled={viewModel.submitting}>
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={onSaveEdit}
              disabled={viewModel.submitting || !viewModel.draftRoleId}
            >
              {viewModel.submitting && (
                <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
              )}
              Save
            </Button>
          </Box>
        ) : (
          <Box display="flex" justifyContent="flex-end" alignItems="center" gridGap={4}>
            <Tooltip title={viewModel.actionTooltip}>
              <span>
                <IconButton
                  color="primary"
                  disabled={viewModel.disabled || isLoadingRoles}
                  onClick={onStartEdit}
                  aria-label={viewModel.actionAriaLabel}
                >
                  {actionIcon}
                </IconButton>
              </span>
            </Tooltip>
            {showRemove && (
              <Tooltip title={CANVAS_ACCESS_ROW_TOOLTIP.REMOVE_ACCESS}>
                <span>
                  <IconButton
                    color="secondary"
                    disabled={viewModel.disabled || submitting}
                    onClick={onRemoveAccess}
                    aria-label={CANVAS_ACCESS_ROW_TOOLTIP.REMOVE_ACCESS}
                    data-testid={`staff-canvas-access-remove-${row.locationKey}`}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

StaffCanvasAccessRow.propTypes = {
  row: PropTypes.shape({
    locationKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    locationName: PropTypes.string,
    canvasAccountId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    canvasRoleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    canvasRoleLabel: PropTypes.string,
    provisioned: PropTypes.bool,
  }).isRequired,
  editingLocationKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  roleOptionsByLocation: PropTypes.object.isRequired,
  rowActionsDisabled: PropTypes.bool.isRequired,
  draftRoleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isSelected: PropTypes.bool.isRequired,
  isLoadingRoles: PropTypes.bool,
  submitting: PropTypes.bool.isRequired,
  onToggleSelected: PropTypes.func.isRequired,
  onLoadRoles: PropTypes.func.isRequired,
  onDraftRoleChange: PropTypes.func.isRequired,
  onStartEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onRemoveAccess: PropTypes.func,
};

export default withStyles(styles)(StaffCanvasAccessRow);
